import React, { ChangeEvent, FormEvent, useCallback, useState } from 'react';
import styles from './SubtitlesUploadModal.module.scss';
import Modal from '@components/modal/Modal';
import { TextInput, TextInputValue } from '@components/text-input/TextInput';
import Button from '@components/button/Button';
import { gql, useMutation } from '@apollo/client';
import { getMediaAbsolutePath, uploadFile } from '@services/File';
import { isEmptyObject, validateIsEmpty } from '@utils/validation';
import { SelectOption, VideoSubtitle } from '@types';
import Select from '@components/select/Select';
import { subtitlesQuery, SubtitlesResponse } from '../subtitles-list/SubtitlesList';
import languages from './languages';
import { useAWSKeys } from '@src/hooks';
import { logger } from '../../utils/logger';

type Props = {
  onClose: () => void;
  id?: string;
  playbackId: string;
};

const createSubtitles = gql`
    mutation CreateSubtitle($input: CreateVideoSubtitleInput!) {
        createVideoSubtitle(input: $input) {
            id, languageCode, name
        }
    }
`;

type CreateSubtitlesResponse = {
  createVideoSubtitle: VideoSubtitle;
};

const emptyErrors = {
  name: '',
  file: '',
};

type EmptyErrors = Partial<Record<keyof typeof emptyErrors, string>>;

const codeOptions: SelectOption[] = languages;

function SubtitlesUploadModal(props: Props) {
  const { onClose, playbackId, id } = props;

  const { awsKeys } = useAWSKeys();

  const [create] = useMutation<CreateSubtitlesResponse>(createSubtitles, {
    onError(e) {
      logger.debug(`[e]`, e);
      alert(e.message);
      setLoading(false);
    },
    update(cache, res) {
      const s = res?.data?.createVideoSubtitle;
      if (s && id) {
        const subtitlesResponse = cache.readQuery<SubtitlesResponse>({ query: subtitlesQuery, variables: { playbackId, assetId: id }});
        cache.writeQuery({
          query: subtitlesQuery,
          variables: { playbackId, assetId: id },
          data: { videoSubtitles: [...subtitlesResponse?.videoSubtitles || [], s] },
        });
      }
    },
    onCompleted(res) {
      onClose();
    }
  });

  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File>();
  const [language, setLanguage] = useState('en');
  const [errors, setErrors] = useState<EmptyErrors>(emptyErrors);

  const handleChangeText = useCallback((setter, key: keyof typeof emptyErrors) => (value: TextInputValue) => {
    setErrors({
      ...errors,
      [key]: '',
    });
    setter(value);
  }, [errors]);

  const validate = () => {
    const errors: EmptyErrors = {};
    if (validateIsEmpty(name)) {
      errors.name = 'Name is required.';
    }

    if (!file) {
      errors.file = 'Select a file.';
    }

    return errors;
  }

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();

    const err = validate();

    if (isEmptyObject(err)) {
      if (!file) {
        return;
      }

      setLoading(true);

      try {
        const res = await uploadFile(awsKeys, file, 'subtitles');

        create({
          variables: {
            input: {
              name,
              languageCode: language,
              url: getMediaAbsolutePath(res),
              playbackId
            }
          }
        });
      } catch (e) {
        setLoading(false);
      }
    } else {
      setErrors(err);
    }
  };

  const handleSelectFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFile(file);
  };

  const handleChangeLanguage = (o: SelectOption) => {
    setLanguage(o.value);
  };

  return (
    <Modal open={true} onClose={onClose}>
      <div className={styles.row}>
        <div className={styles.title}>Upload subtitles</div>
      </div>
      <form className={styles.wrapper} onSubmit={handleSave}>
        <div className={styles.row}>
          <TextInput
            hasError={!!errors.name}
            subTitle={errors.name}
            label={'Name'}
            id={'name'}
            placeholder={'Name'}
            value={name}
            onChange={handleChangeText(setName, 'name')}
          />
        </div>
        <div className={styles.row}>
          <Select value={language} label={'Language'} onChange={handleChangeLanguage} options={codeOptions}/>
        </div>
        <div className={styles.row}>
          <input type={'file'} accept={'.vtt,.srt'} onChange={handleSelectFile}/>
        </div>
        <div>
          <Button size={'extraSmall'} disabled={loading} className={styles.addButton} type={'submit'} label={'Upload'} />
        </div>
      </form>
    </Modal>
  );
}

export default SubtitlesUploadModal;
